import { useEffect, useRef, useState } from 'react';
import { monthNames } from '../utils/reportingPeriod';
import { peso } from '../utils/formatCurrency';

// Chart colours. Series hues are validated categorical slots (checked on a white surface);
// ink and grid tokens keep text and rules quiet so the data is the loudest thing on the card.
const INK = { primary: '#0b0b0b', secondary: '#52514e', grid: '#e1e0d9', axis: '#c3c2b7', surface: '#ffffff' };
const SERIES = { collected: '#2a78d6', disbursed: '#eb6834' };

const AXIS_FONT = 13;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const trimNumber = (n) => Number(n.toFixed(1)).toString();
const compactPeso = (value) =>
  value >= 1e6 ? `₱${trimNumber(value / 1e6)}M` : value >= 1e3 ? `₱${trimNumber(value / 1e3)}K` : `₱${value}`;

// Round axis ticks (0, 1,000, 2,000 ...) that cover `max`.
function niceTicks(max, count = 4) {
  if (max <= 0) return [0, 1];
  const raw = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw);
  const top = Math.ceil(max / step) * step;
  return Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
}

function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

function Tooltip({ x, y, width, children }) {
  const flip = x > width / 2;
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute z-10 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-md"
      style={{ left: x, top: y, transform: `translate(${flip ? 'calc(-100% - 12px)' : '12px'}, 8px)` }}
    >
      {children}
    </div>
  );
}

function LineKey({ color }) {
  return <span className="inline-block h-0.5 w-4 shrink-0 rounded" style={{ background: color }} />;
}

function EmptyNote({ children }) {
  return <p className="py-10 text-center text-sm text-gray-600">{children}</p>;
}

// Every chart has a table twin, so no value depends on colour or a tooltip.
function DataTable({ columns, rows }) {
  return (
    <div className="-mx-4 overflow-x-auto">
      <table className="w-full text-sm text-left border-collapse">
        <thead>
          <tr className="border-b border-gray-200 text-navy">
            {columns.map((column, i) => (
              <th
                key={column.key}
                className={`py-2 pr-4 font-semibold whitespace-nowrap ${i === 0 ? 'pl-4' : ''} ${
                  column.numeric ? 'text-right' : ''
                }`}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.key} className={row.emphasis ? 'font-semibold' : ''}>
              {columns.map((column, i) => (
                <td
                  key={column.key}
                  className={`py-2 pr-4 text-gray-800 whitespace-nowrap ${i === 0 ? 'pl-4' : ''} ${
                    column.numeric ? 'text-right tabular-nums' : ''
                  }`}
                >
                  {row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChartCard({ title, subtitle, table, className = '', children }) {
  const [showTable, setShowTable] = useState(false);

  return (
    <section className={`w-full bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
      <div className="-mx-4 -mt-4 mb-3 flex items-center justify-between gap-3 rounded-t-lg bg-navy-light px-4 py-2 text-white">
        <h2 className="text-md lg:text-lg font-semibold">{title}</h2>
        {table && (
          <button
            type="button"
            onClick={() => setShowTable((shown) => !shown)}
            aria-pressed={showTable}
            className="whitespace-nowrap text-sm font-medium underline underline-offset-2 hover:text-gold"
          >
            {showTable ? 'View chart' : 'View table'}
          </button>
        )}
      </div>
      {subtitle && <p className="mb-3 text-sm text-gray-600">{subtitle}</p>}
      {showTable && table ? table : children}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Monthly trend: collected vs disbursed                               */
/* ------------------------------------------------------------------ */

function TrendPlot({ points }) {
  const [ref, width] = useElementWidth();
  const [active, setActive] = useState(null);

  const height = 260;
  const margin = { top: 12, right: 60, bottom: 30, left: 52 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = height - margin.top - margin.bottom;
  const count = points.length;

  const ticks = niceTicks(Math.max(...points.flatMap((p) => [p.collected, p.disbursed])));
  const yMax = ticks[ticks.length - 1];
  const x = (i) => (count > 1 ? margin.left + (i * innerW) / (count - 1) : margin.left + innerW / 2);
  const y = (value) => margin.top + innerH - (value / yMax) * innerH;

  const pathFor = (key) => points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p[key])}`).join(' ');
  const labelStep = width / count < 40 ? 2 : 1;

  const handlePointerMove = (event) => {
    if (count < 2) return setActive(0);
    const rect = ref.current.getBoundingClientRect();
    const ratio = (event.clientX - rect.left - margin.left) / innerW;
    return setActive(clamp(Math.round(ratio * (count - 1)), 0, count - 1));
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') setActive((i) => clamp((i ?? count) - 1, 0, count - 1));
    else if (event.key === 'ArrowRight') setActive((i) => clamp((i ?? -1) + 1, 0, count - 1));
    else if (event.key === 'Escape') setActive(null);
    else return;
    event.preventDefault();
  };

  const last = points[count - 1];
  const endLabelsFit = Math.abs(y(last.collected) - y(last.disbursed)) >= 16;
  const activePoint = active != null ? points[active] : null;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
        <span className="flex items-center gap-2">
          <LineKey color={SERIES.collected} /> Collected
        </span>
        <span className="flex items-center gap-2">
          <LineKey color={SERIES.disbursed} /> Disbursed
        </span>
      </div>

      <div ref={ref} className="relative w-full" style={{ height }}>
        {width > 0 && (
          <svg
            width={width}
            height={height}
            role="img"
            tabIndex={0}
            aria-label="Line chart of collected and disbursed amounts for each month of the school year. Use the left and right arrow keys to read each month, or switch to the table view."
            onKeyDown={handleKeyDown}
            onBlur={() => setActive(null)}
            className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-light"
          >
            {ticks.map((tick) => (
              <g key={tick}>
                <line
                  x1={margin.left}
                  x2={margin.left + innerW}
                  y1={y(tick)}
                  y2={y(tick)}
                  stroke={tick === 0 ? INK.axis : INK.grid}
                  strokeWidth="1"
                />
                <text x={margin.left - 8} y={y(tick) + 4} textAnchor="end" fontSize={AXIS_FONT} fill={INK.secondary}>
                  {compactPeso(tick)}
                </text>
              </g>
            ))}

            {points.map((p, i) =>
              i % labelStep === 0 ? (
                <text
                  key={`${p.year}-${p.month}`}
                  x={x(i)}
                  y={height - 8}
                  textAnchor="middle"
                  fontSize={AXIS_FONT}
                  fill={INK.secondary}
                >
                  {monthNames[p.month - 1].slice(0, 3)}
                </text>
              ) : null
            )}

            {activePoint && (
              <line x1={x(active)} x2={x(active)} y1={margin.top} y2={margin.top + innerH} stroke={INK.axis} strokeWidth="1" />
            )}

            {['collected', 'disbursed'].map((key) => (
              <g key={key}>
                {count > 1 && (
                  <path
                    d={pathFor(key)}
                    fill="none"
                    stroke={SERIES[key]}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
                {[count - 1, ...(active != null && active !== count - 1 ? [active] : [])].map((i) => (
                  <circle
                    key={i}
                    cx={x(i)}
                    cy={y(points[i][key])}
                    r="5"
                    fill={SERIES[key]}
                    stroke={INK.surface}
                    strokeWidth="2"
                  />
                ))}
                {endLabelsFit && (
                  <text
                    x={x(count - 1) + 12}
                    y={y(last[key]) + 4}
                    fontSize={AXIS_FONT}
                    fontWeight="600"
                    fill={INK.primary}
                  >
                    {compactPeso(last[key])}
                  </text>
                )}
              </g>
            ))}

            <rect
              x={margin.left - 12}
              y={margin.top}
              width={innerW + 24}
              height={innerH}
              fill="transparent"
              onPointerMove={handlePointerMove}
              onPointerLeave={() => setActive(null)}
            />
          </svg>
        )}

        {activePoint && (
          <Tooltip x={x(active)} y={margin.top} width={width}>
            <p className="mb-1 font-semibold text-gray-900">
              {monthNames[activePoint.month - 1]} {activePoint.year}
            </p>
            {[
              ['collected', 'Collected'],
              ['disbursed', 'Disbursed'],
            ].map(([key, label]) => (
              <p key={key} className="flex items-center gap-2 whitespace-nowrap">
                <LineKey color={SERIES[key]} />
                <strong className="text-gray-900">{peso(activePoint[key])}</strong>
                <span className="text-gray-600">{label}</span>
              </p>
            ))}
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function TrendChart({ points, loading, schoolYearText }) {
  const hasData = points.some((p) => p.collected || p.disbursed);

  let body;
  if (!schoolYearText) {
    body = <EmptyNote>Choose a school year to see the month-by-month trend.</EmptyNote>;
  } else if (points.length === 0) {
    body = <EmptyNote>{loading ? 'Loading…' : 'No monthly data is available yet.'}</EmptyNote>;
  } else if (!hasData) {
    body = <EmptyNote>No collections or disbursements recorded for {schoolYearText} yet.</EmptyNote>;
  } else {
    body = (
      <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
        <TrendPlot points={points} />
      </div>
    );
  }

  const table = hasData ? (
    <DataTable
      columns={[
        { key: 'month', label: 'Month' },
        { key: 'collected', label: 'Collected', numeric: true },
        { key: 'disbursed', label: 'Disbursed', numeric: true },
      ]}
      rows={points.map((p) => ({
        key: `${p.year}-${p.month}`,
        month: `${monthNames[p.month - 1]} ${p.year}`,
        collected: peso(p.collected),
        disbursed: peso(p.disbursed),
      }))}
    />
  ) : null;

  return (
    <ChartCard
      title="Monthly Collections vs Disbursements"
      subtitle={schoolYearText ? `${schoolYearText}, month by month` : null}
      table={table}
    >
      {body}
    </ChartCard>
  );
}

export default TrendChart;
